import React, { useState, useEffect } from 'react';
import DateRangePickerComponent from './DateRangePickerComponent';
import './Holiday.css';
import BookedLeaveTable from './BookedLeaveTable';
import Dropdown from "../../../Components/DropDownSelect/Dropdown";
import CompanyCalander from "../../../Components/ResourceCalander/ResourceCalander";
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { useMsal } from "@azure/msal-react";
import '../../../Components/YesNoButtonsCSS/YesNoButtons.css';
import { useDispatch, useSelector } from '../../../redux/store';
import { fetchUserLeaves } from '../../../redux/slices/holiday';
import {
  cancelProfileLeaveRequest,
  fetchCalendarData,
  fetchCalendarDataWithParams,
  fetchProfileHolidayAllowance,
  submitProfileLeaveRequest,
} from '../../../redux/slices/profileHoliday';

const leave_type_options = [
  "Holiday",
  "Study Leave",
  "University Trip",
  "Jury Service",
];

const leave_length_options = [
  "Full Day",
  "Half Day (AM)",
  "Half Day (PM)"
];

function Holiday() {
  const [reload, setReload] = useState(0);

  // Function to trigger data reload
  const handleDataReload = () => {
    setReload(prev => prev + 1);
  };
  const [leaveType, setLeaveType] = useState("");
  const [leaveLength, setLeaveLength] = useState("");
  const [comment, setComment] = useState("");
  const [calendarData, setCalendarData] = useState("");
  const [apiResponse, setApiResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isDataFetched, setIsDataFetched] = useState(false);
  const { accounts } = useMsal();
  const account = accounts[0];
  
  // Redux integration
  const dispatch = useDispatch();
  const { leaves, isLoading } = useSelector(state => state.holiday);
  const [RequestedHolidayDays, setRequestedHolidayDays] = useState([]);
  const [RequestedStudyDays, setRequestedStudyDays] = useState([]);
  const [holidayDays, setHolidayDays] = useState([]);
  const [studyDays, setStudyDays] = useState([]);

  const [serviceDays, setServiceDays] = useState([]);
  const [carryDays, setCarryDays] = useState([]);
  const [contractAllowance, setContractAllowance] = useState([]);
  const [holidayPurchased, setHolidayPurchased] = useState([]);
  const [holidayAdjustment, setHolidayAdjustment] = useState([]);
  
  const [totalHolidayAllowance, setTotalHolidayAllowance] = useState([]);
  const [totalStudyAllowance, setTotalStudyAllowance] = useState([]);
  const [isResponseShown, setIsResponseShown] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [leaveDates, setLeaveDates] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: 'selection',
    }
  ]);

  const handleFocus = () => {
    setIsFocused(true);
  }

  const handleBlur = () => {
    if (comment === '') {
      setIsFocused(false);
    }
  }

  useEffect(() => {
    // Fetch leave data via Redux
    dispatch(fetchUserLeaves(account.username, 0)); // 0 for current user

    const allowancePromise = dispatch(fetchProfileHolidayAllowance({ userEmail: account.username }));
    const calendarPromise = dispatch(fetchCalendarData());

    Promise.all([allowancePromise.unwrap(), calendarPromise.unwrap()])
      .then(([fetchedAllowance, fetchedCalendarData]) => {
        setCalendarData(fetchedCalendarData);
        setHolidayDays(fetchedAllowance.holidayAllowance);
        setStudyDays(fetchedAllowance.studyAllowance);
        setTotalHolidayAllowance(fetchedAllowance.totalHolidayAllowance);
        setCarryDays(fetchedAllowance.carryDays);
        setHolidayPurchased(fetchedAllowance.holidayPurchased);
        setHolidayAdjustment(fetchedAllowance.holidayAdjustment);
        setContractAllowance(fetchedAllowance.contractAllowance);
        setServiceDays(fetchedAllowance.serviceDays);
        setTotalStudyAllowance(fetchedAllowance.totalStudyAllowance);
        setRequestedStudyDays(fetchedAllowance.studyRequested);
        setRequestedHolidayDays(fetchedAllowance.holidayRequested);
        setIsDataFetched(true);
      })
      .catch((e) => {
        if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') return;
        console.error('Holiday page load failed', e);
      });

    return () => {
      allowancePromise?.abort?.();
      calendarPromise?.abort?.();
    };
  }, [reload, dispatch, account.username]);


  const handleCancelationReload = async (currentId) => {
    setLoading(true);
    try {
      const data = await dispatch(cancelProfileLeaveRequest({ userEmail: account.username, leaveId: currentId })).unwrap();
      setApiResponse(data?.message || 'Leave request cancelled.');
      setReload(reload + 1);
    } catch (error) {
      console.error("Error in handleCancelationReload:", error);
      setApiResponse('Error cancelling leave request (please try again).');
    } finally {
      setLoading(false);
    }
    setIsResponseShown(true);
  };


  const toLocalISOString = (date) => {
    let tzo = -date.getTimezoneOffset(),
      dif = tzo >= 0 ? '+' : '-',
      pad = function (num) {
        let norm = Math.floor(Math.abs(num));
        return (norm < 10 ? '0' : '') + norm;
      };

    return date.getFullYear() +
      '-' + pad(date.getMonth() + 1) +
      '-' + pad(date.getDate());
  }

  const handleSubmit = async () => {
    setLoading(true);

    const bookingDetails = {
      leaveType,
      leaveLength,
      leaveDates: leaveDates.map(({ startDate, endDate, key }) => ({
        startDate: toLocalISOString(startDate),
        endDate: toLocalISOString(endDate),
        key
      })),
      comment,
      email: account.username,
    };

    try {
      const data = await dispatch(submitProfileLeaveRequest({ bookingDetails })).unwrap();
      setApiResponse(data?.message || 'Leave request submitted.');
    } catch (error) {
      console.error('Error:', error);
      setApiResponse('Error occurred');
    }
    setReload(reload + 1);
    setLeaveType("");
    setLeaveLength("");
    setLeaveDates([
      {
        startDate: new Date(),
        endDate: new Date(),
        key: 'selection',
      }
    ]);
    setComment("");
    setIsResponseShown(true);
    setLoading(false);
  };

  const CalanderAPItrigger = async (changeData) => {
    try {
      const data = await dispatch(fetchCalendarDataWithParams({ params: changeData })).unwrap();
        setCalendarData(data);
    } catch (error) {
      console.error('Error:', error);
      setCalendarData('Error occurred');
    }
  }

  return (
    <>
      {isDataFetched ? (
        <div className='ProfileMainContent'>
          <div className='holidayManagment'>
            <div className='bookedLeaveInfo'>
              <div className='title' style={{ width: '100%' }}>Current Year Leave Overview</div>

              {/* Top Row */}
              <div className='daysLeft' style={{ marginTop: '28px' }}>
                <div style={{ marginRight: '6px' }} className='BookedAndAllowanceDays'>
                  <div style={{ marginBottom: '8px' }} className='RequestedDays'>
                    <div style={{ display: 'block', marginTop: '0px', fontSize: '30px' }} className='studyDaysNum'>{totalHolidayAllowance}</div>
                    <div className='RequestedDaysText'>
                      <span style={{ display: 'block', marginTop: '3px', fontSize: '14px', width: '62px' }}>Allowance</span>
                    </div>
                  </div>
                  <div className='RequestedDays'>
                    <div style={{ display: 'block', marginTop: '0px', fontSize: '30px' }} className='studyDaysNum'>{totalHolidayAllowance - holidayDays}</div>
                    <div className='RequestedDaysText'>
                      <span style={{ display: 'block', marginTop: '3px', fontSize: '14px', width: '62px' }}>Booked</span>
                    </div>
                  </div>
                </div>
                <div className='holidayDays'>
                  <div className='holidayDaysNum'>{holidayDays}</div>
                  <div className='holidayDaysText'>
                    <span style={{ display: 'block', marginTop: '4px', fontSize: '14px' }}>Holiday</span>
                    <span style={{ display: 'block', marginTop: '-3px', fontSize: '14px' }}>Days</span>
                    <span style={{ display: 'block', marginTop: '-3px', fontSize: '14px' }}>Remaining</span>
                  </div>
                </div>
                <div className='studyDays'>
                  <div className='studyDaysNum'>{studyDays}</div>
                  <div className='studyDaysText'>
                    <span style={{ display: 'block', marginTop: '3px', fontSize: '14px' }}>Study</span>
                    <span style={{ display: 'block', marginTop: '-3px', fontSize: '14px' }}>Days</span>
                    <span style={{ display: 'block', marginTop: '-3px', fontSize: '14px' }}>Remaining</span>
                  </div>
                </div>
                <div style={{ marginLeft: '6px' }} className='BookedAndAllowanceDays'>
                  <div style={{ marginBottom: '8px' }} className='RequestedDays'>
                    <div style={{ display: 'block', marginTop: '0px', fontSize: '30px' }} className='studyDaysNum'>{totalStudyAllowance}</div>
                    <div className='RequestedDaysText'>
                      <span style={{ display: 'block', marginTop: '3px', fontSize: '14px', width: '62px' }}>Allowance</span>
                    </div>
                  </div>
                  <div className='RequestedDays'>
                    <div style={{ display: 'block', marginTop: '0px', fontSize: '30px' }} className='studyDaysNum'>{totalStudyAllowance - studyDays}</div>
                    <div className='RequestedDaysText'>
                      <span style={{ display: 'block', marginTop: '3px', fontSize: '14px', width: '62px' }}>Booked</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom Row */}
              <div className='daysLeft' style={{ marginBottom: '28px' }}>
                <div style={{ marginRight: '6px' }} className='BookedAndAllowanceDays'>
                  <div className='RequestedDays'>
                    <div style={{ display: 'block', marginTop: '0px', fontSize: '30px' }} className='studyDaysNum'>{contractAllowance}</div>
                    <div className='RequestedDaysText'>
                      <span style={{ display: 'block', marginTop: '3px', fontSize: '14px', width: '62px' }}>Contract</span>
                    </div>
                  </div>
                </div>

                <div style={{ marginRight: '6px' }} className='BookedAndAllowanceDays'>
                  <div className='RequestedDays'>
                    <div style={{ display: 'block', marginTop: '0px', fontSize: '30px' }} className='studyDaysNum'>{serviceDays}</div>
                    <div className='RequestedDaysText'>
                      <span style={{ display: 'block', marginTop: '3px', fontSize: '14px', width: '62px' }}>Service</span>
                    </div>
                  </div>
                </div>

                <div style={{ marginRight: '6px' }} className='BookedAndAllowanceDays'>
                  <div className='RequestedDays'>
                    <div style={{ display: 'block', marginTop: '0px', fontSize: '30px' }} className='studyDaysNum'>{carryDays}</div>
                    <div className='RequestedDaysText'>
                      <span style={{ display: 'block', marginTop: '3px', fontSize: '14px', width: '62px' }}>Carry</span>
                      <span style={{ display: 'block', marginTop: '-5px', fontSize: '14px', width: '62px' }}>Over</span>
                    </div>
                  </div>
                </div>

                <div style={{ marginLeft: '6px' }} className='BookedAndAllowanceDays'>
                  <div className='RequestedDays'>
                    <div style={{ display: 'block', marginTop: '0px', fontSize: '30px' }} className='studyDaysNum'>{holidayPurchased}</div>
                    <div className='RequestedDaysText'>
                      <span style={{ display: 'block', marginTop: '3px', fontSize: '14px', width: '70px' }}>Purchased</span>
                    </div>
                  </div>
                </div>

                <div style={{ marginLeft: '6px' }} className='BookedAndAllowanceDays'>
                  <div className='RequestedDays'>
                    <div style={{ display: 'block', marginTop: '0px', fontSize: '30px' }} className='studyDaysNum'>{holidayAdjustment}</div>
                    <div className='RequestedDaysText'>
                      <span style={{ display: 'block', marginTop: '3px', fontSize: '14px', width: '80px' }}>Adjustments</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className='bookedLeaveBox' style={{ display: isDataFetched ? 'block' : 'none', maxHeight: '160px' }}>
                <BookedLeaveTable 
                  setIsDataFetched={setIsDataFetched} 
                  handleCancelationReload={handleCancelationReload}
                  remainingHolidays={holidayDays}
                  onDataReload={handleDataReload}
                />
              </div>
            </div>

            <div className='bookedLeaveInfo'>
              <div className='title' style={{ width: 'calc(100% - 24px)', marginLeft: '24px' }}>Book Leave</div>
              <div className='bookingLeave'>
                <div className='bookingLeaveInteraction'>
                  <div className='selectsSection'>
                    <div className='typeOfLeave'>
                      <div className='bookingLeaveLabel'>Type of Leave</div>
                      <div className="dropdowns">
                        <Dropdown options={leave_type_options} selectedValue={leaveType} onValueChange={setLeaveType} />
                      </div>
                    </div>
                    <div className='leaveLength'>
                      <div className='bookingLeaveLabel'>Length of Leave</div>
                      <div className="dropdowns">
                        <Dropdown options={leave_length_options} selectedValue={leaveLength} onValueChange={setLeaveLength} />
                      </div>
                    </div>
                  </div>
                  <div className='bookingLeaveRow'>
                    <div className='bookingLeaveLabel'>Date of Leave</div>
                    <DateRangePickerComponent selectedDates={leaveDates} onDatesChange={setLeaveDates} />
                  </div>
                  <div className='bookingLeaveRowComment'>
                    <textarea className={isFocused ? 'bookingLeaveComment' : 'nonBookingLeaveComment'} id='comment' placeholder={isFocused ? '' : 'Comment...'} onFocus={handleFocus} onBlur={handleBlur} value={comment} onChange={(e) => setComment(e.target.value)}></textarea>
                  </div>
                  <div className='submitdiv'>
                    <button className='bookingLeaveSubmit' onClick={handleSubmit}>SUBMIT</button>
                  </div>
                </div>

                {loading ? // Here is the code for displaying error/success messages
                  <div className='ProfileHolidayOverlay'>
                    <Box sx={{ display: 'flex' }}>
                      <CircularProgress size={65} />
                    </Box>
                  </div>
                  :
                  isResponseShown && (
                    <div className='ProfileHolidayOverlay'>
                      <div className='ProfileHolidayResponseBox'>
                        <div
                          style={{
                            fontSize: 16,
                            fontFamily: 'PT Sans',
                            textAlign: 'center',
                            margin: '20px',
                            marginLeft: '25px',
                            marginRight: '25px'
                          }}
                          dangerouslySetInnerHTML={{ __html: apiResponse }}
                        >
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <button className='ProfileHolidayButton' onClick={() => setIsResponseShown(false)}>
                            OK
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                }
              </div>
            </div>
          </div>

          <div className='title2'>Company Leave Calendar</div>
          <CompanyCalander resourceData={calendarData} CalanderAPItrigger={CalanderAPItrigger} UserEmail={account.username} />

        </div >
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', paddingTop: '250px', justifyContent: 'center' }}>
          <CircularProgress size={65} />
        </Box>
      )
      }
    </>
  );
}

export default Holiday;